import { Controller } from "@hotwired/stimulus";

// A class attribute value is a space separated list and
// classList refuses an empty string, so an unset value
// has to drop out rather than reach classList.remove().
function classNames(value) {
  return value.split(" ").filter(Boolean);
}

// This controller manages the dynamic behavior of modifier fields based on
// the parent group's price type. It handles:
// - Updating helper text and placeholders
// - Showing/hiding min/max fields
// - Managing required attributes
// - Listening for price type changes from the parent form
export default class extends Controller {
  static targets = [
    "priceHelperText",
    "priceInput",
    "minMaxFields",
    "priceBadge",
    "titleText",
    "nameInput",
    "gridContainer",
  ];
  static values = {
    // The current price type of the modifier group
    // (additive/override/highest)
    groupPriceType: String,
    // Text values for different price types
    additiveText: String,
    overrideText: String,
    highestText: String,
    additivePlaceholder: String,
    overridePlaceholder: String,
    highestPlaceholder: String,
    newModifierText: String,
    // Grid classes for different price types
    additiveGridClass: { type: String, default: "grid-cols-1 md:grid-cols-2" },
    overrideGridClass: { type: String, default: "grid-cols-1" },
    highestGridClass: { type: String, default: "grid-cols-1" },
  };

  connect() {
    // Find the parent form's price type. It is the live
    // answer; the value the server rendered is what a
    // field added from the template carries.
    const parentForm = this.element.closest(
      "[data-controller*='modifier-group-form']"
    );
    const priceTypeInput = parentForm?.querySelector(
      "input[name='modifier_group[price_type]']:checked"
    );

    this.updateFields(priceTypeInput?.value || this.groupPriceTypeValue);
  }

  // Listen for the priceTypeChanged event from the parent form
  priceTypeChanged(event) {
    this.updateFields(event.detail.priceType);
  }

  updateFields(priceType) {
    if (!priceType) return; // Guard against undefined state

    // Kept in step so the price badge, which reads it
    // long after connect, prices an option the way the
    // group does now rather than the way it did on page
    // load.
    this.groupPriceTypeValue = priceType;
    const presentation = this.presentationFor(priceType);

    // Update helper text based on price type
    if (this.hasPriceHelperTextTarget) {
      this.priceHelperTextTarget.textContent = presentation.helperText;
    }

    // Update price input placeholder based on price type
    if (this.hasPriceInputTarget) {
      this.priceInputTarget.placeholder = presentation.placeholder;
    }

    // Show/hide min/max fields and manage their required state
    this.minMaxFieldsTargets.forEach((field) => {
      field.classList.toggle("hidden", presentation.fixedQuantity);
      const input = field.querySelector("input");
      if (input) {
        if (presentation.fixedQuantity) {
          input.value = "1";
          input.required = false;
        } else {
          input.required = true;
        }
      }
    });

    // Update grid layout based on price type
    if (this.hasGridContainerTarget) {
      this.gridClassValues().forEach((classes) => {
        classNames(classes).forEach((cls) => {
          this.gridContainerTarget.classList.remove(cls);
        });
      });

      classNames(presentation.gridClass).forEach((cls) => {
        this.gridContainerTarget.classList.add(cls);
      });
    }
  }

  // A `highest` group prices an option outright the way
  // `override` does, and refuses quantity modifiers for
  // its own reason: "2x Romana" would satisfy a two half
  // pizza while meaning a whole one.
  presentationFor(priceType) {
    switch (priceType) {
      case "override":
        return {
          helperText: this.overrideTextValue,
          placeholder: this.overridePlaceholderValue,
          gridClass: this.overrideGridClassValue,
          absolutePrice: true,
          fixedQuantity: true,
        };
      case "highest":
        return {
          helperText: this.highestTextValue,
          placeholder: this.highestPlaceholderValue,
          gridClass: this.highestGridClassValue,
          absolutePrice: true,
          fixedQuantity: true,
        };
      default:
        return {
          helperText: this.additiveTextValue,
          placeholder: this.additivePlaceholderValue,
          gridClass: this.additiveGridClassValue,
          absolutePrice: false,
          fixedQuantity: false,
        };
    }
  }

  gridClassValues() {
    return [
      this.additiveGridClassValue,
      this.overrideGridClassValue,
      this.highestGridClassValue,
    ];
  }

  // Update the price badge in the header when the price input changes
  updateHeaderPrice() {
    if (!this.hasPriceBadgeTarget) return;

    const price = this.priceInputTarget.value;
    if (price) {
      // Format the price with 2 decimal places
      const formattedPrice = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(price);

      // Show the badge and update its content
      this.priceBadgeTarget.classList.remove("hidden");
      this.priceBadgeTarget.textContent = this.presentationFor(
        this.groupPriceTypeValue
      ).absolutePrice
        ? formattedPrice
        : "+" + formattedPrice;
    } else {
      // Hide the badge if there's no price
      this.priceBadgeTarget.classList.add("hidden");
    }
  }

  // Update the title in the header when the name input changes
  updateTitle() {
    if (!this.hasTitleTextTarget) return;

    const name = this.nameInputTarget.value;
    this.titleTextTarget.textContent = name || this.newModifierTextValue;
  }
}
